import React, { useState, useCallback } from 'react';
import { Toolbar, IconButton, useScrollTrigger, Slide, useMediaQuery } from '@mui/material';

import { ReactComponent as DrSmileImpressLogoDesktop } from 'mui/assets/icons/DRSXIMPRESS-desktop.svg';
import { ReactComponent as DrSmileImpressLogo } from 'mui/assets/icons/DRSXIMPRESS.svg';
import { ReactComponent as DrSmileLogoIcon } from 'mui/assets/icons/drsmile-black-logo.svg';
import { ReactComponent as MobileMenuIcon } from 'mui/assets/icons/burger-menu.svg';
import { ReactComponent as MobileCloseIcon } from 'mui/assets/icons/close-icon-rounded.svg';

import Link from 'mui/components/elements/Link';
import LocaleSwitcher from 'mui/components/elements/DS_LocaleSwitcher/DS_LocaleSwitcher';
import { isDRSmileInactive } from '../../../../../data/isDRSmileInactive';

import Popup from '../../../../../components/common/Popup';
import { BOOKING_CTA_CLASS } from '../../../../assets/constants/gtmConstants';
import StepsProgressBar from '../../../../../components/StepsProgressBar';
import NewsBanner from '../../../../../components/common/NewsBanner';
import getURLParameter from '../../../../../utils/getURLParams';
import { useHeaderStaticQuery } from './hooks';
import findCurrentNewsBanner from './utils/findCurrentNewsBanner';

import MobileDrawer from './elements/MobileDrawer';

import {
  StyledHeader,
  StyledAppBar,
  StyledSvgLogo,
  StyledSwipeableDrawer,
  StyledIconsWrapper,
  StyledWrapperProgressBar,
  StyledWrapperTabletVisibleOnly,
  StyledWrapperDesktopVisibleOnly,
} from './Global_DS_Header_Styles';
import NavButtons from './elements/NavButtons';
import IconLinks from './elements/IconLinks';
import MenuLinks from './elements/MenuLinks/MenuLinks';

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Global_DS_Header = ({
  data,
  data: {
    pathname,
    hasProgressBar,
    CTAButton,
    chatLink,
    shopLink,
    menuOne,
    menuOneLabel,
    menuTwo,
    menuTwoLabel,
    directLinks,
  },
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const utmCampaign = typeof window !== 'undefined' ? getURLParameter('utm_campaign') : null;
  const { allNewsBanners, popup } = useHeaderStaticQuery();
  const [newsBanner] = useState(() => findCurrentNewsBanner({ allNewsBanners, pathname, utmCampaign }));
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('lg'), {
    defaultMatches: false, // Set this to match your server-side rendering needs
    noSsr: false, // Set to true if you want to disable SSR support
  });

  const isTablet = useMediaQuery(theme => theme.breakpoints.between('md', 'lg'), {
    defaultMatches: false, // Set this to match your server-side rendering needs
    noSsr: false, // Set to true if you want to disable SSR support
  });

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prevState => !prevState);
  }, []);

  const logoComponent = isDesktop ? DrSmileImpressLogoDesktop : DrSmileImpressLogo;
  const brandLogo = isDRSmileInactive ? DrSmileLogoIcon : logoComponent;
  const logo =
    pathname === '/' ? (
      <StyledSvgLogo component={brandLogo} inheritViewBox={true} />
    ) : (
      <Link url="/" isInternal ariaLabel="Go to homepage">
        <StyledSvgLogo component={brandLogo} inheritViewBox={true} />
      </Link>
    );

  function removeTrailingSlash(url) {
    if (!url) return url;
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  return (
    <HideOnScroll>
      <StyledHeader mobileOpen={mobileOpen}>
        {popup ? (
          <Popup
            {...popup}
            gtmTrackingInfo={{ gtmClassName: BOOKING_CTA_CLASS, gtmElementId: `${BOOKING_CTA_CLASS}Popup` }}
          />
        ) : null}

        <div>
          {newsBanner && !mobileOpen ? <NewsBanner newsBanner={newsBanner} /> : null}
          <StyledAppBar $isDRSmileInactive={isDRSmileInactive} component="section">
            <Toolbar data-test="toolbar" component="nav">
              {logo}
              <StyledWrapperTabletVisibleOnly>
                {isTablet ? <NavButtons CTAButton={CTAButton} /> : null}
              </StyledWrapperTabletVisibleOnly>{' '}
              <StyledWrapperDesktopVisibleOnly>
                <MenuLinks
                  menuOne={menuOne}
                  menuOneLabel={menuOneLabel}
                  menuTwo={menuTwo}
                  menuTwoLabel={menuTwoLabel}
                  directLinks={directLinks}
                  ctaButtonURL={removeTrailingSlash(CTAButton.url)}
                />
                {isDesktop ? <NavButtons CTAButton={CTAButton} /> : null}
                <StyledIconsWrapper>
                  <IconLinks shopLink={shopLink} chatLink={chatLink} />
                  <LocaleSwitcher />
                </StyledIconsWrapper>
              </StyledWrapperDesktopVisibleOnly>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { sm: 'block', lg: 'none' } }}
              >
                {mobileOpen ? <MobileCloseIcon /> : <MobileMenuIcon />}
              </IconButton>
            </Toolbar>
            {hasProgressBar ? (
              <StyledWrapperProgressBar>
                <StepsProgressBar />
              </StyledWrapperProgressBar>
            ) : null}
          </StyledAppBar>
        </div>

        <StyledSwipeableDrawer
          data-test="swipe-drawer-mobile"
          open={isDesktop ? false : mobileOpen}
          onClose={handleDrawerToggle}
          onOpen={handleDrawerToggle}
          disableDiscovery={true}
          disableSwipeToOpen={true}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { lg: 'none' },
          }}
        >
          <MobileDrawer {...data} />
        </StyledSwipeableDrawer>
      </StyledHeader>
    </HideOnScroll>
  );
};

export default Global_DS_Header;
